/**
 * TrivenyHero — the canonical, pixel-exact Triveny public hero.
 *
 * This is the SINGLE source of truth for the "cycling hero" look (Design /
 * Market / Engage statements, post-login launcher stack, top nav, footer). It
 * is used in three places so they are guaranteed pixel-identical:
 *   1. TrivenyPublicHome — the built-in fallback at triveny.ai.
 *   2. PublicBlockRenderer — when a Studio page is a single `triveny_hero`
 *      block, so a page DESIGNED in Studio renders the exact same hero.
 *   3. The Studio editor preview (cycleMs=0 to hold a static frame).
 *
 * All styling lives in the dedicated home stylesheet src/styles/triveny-home.css
 * (`.triveny-home` scope), loaded globally in main.tsx. Editable content is
 * limited to the three pillar statements; the launcher set is the canonical
 * app-home mirror and always routes to login.
 */

import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutTemplate, Mail, ClipboardList,
  Send, Target, Users, Eye, PhoneCall, BarChart3,
  KanbanSquare, Brain, PlusCircle, CalendarClock, Inbox, Headphones,
  ArrowRight,
} from 'lucide-react';
import { resolveIcon } from '../../modules/web-presence-v2/icons';

export type Pillar = 'Design' | 'Market' | 'Engage';

export const PILLAR_ORDER: Pillar[] = ['Design', 'Market', 'Engage'];

export const DEFAULT_STATEMENTS: Record<Pillar, string> = {
  Design: 'Prompt it. Create it.',
  Market: 'All your GTM tools in one place.',
  Engage: 'Move every deal forward.',
};

// Per-pillar launchers — mirror EXACTLY what the app home (app.triveny.ai →
// TrivenyHome PILLARS) presents post-login: a primary launcher + the secondary
// action chips. On the public site every one routes to login.
interface LauncherSpec {
  primary: { icon: LucideIcon; title: string; sub: string };
  secondary: { icon: LucideIcon; title: string }[];
}
const LAUNCHERS: Record<Pillar, LauncherSpec> = {
  Design: {
    primary: { icon: LayoutTemplate, title: 'Create a landing page', sub: 'Prompt → published page in minutes' },
    secondary: [
      { icon: Mail, title: 'Design an email' },
      { icon: ClipboardList, title: 'Build a form' },
    ],
  },
  Market: {
    primary: { icon: Send, title: 'Launch a campaign', sub: 'Email · SMS · voice, one flow' },
    secondary: [
      { icon: Target, title: 'Define an ICP' },
      { icon: Users, title: 'Build a lead list' },
      { icon: Eye, title: 'Identify visitors' },
      { icon: PhoneCall, title: 'Cold outreach' },
      { icon: BarChart3, title: 'View analytics' },
    ],
  },
  Engage: {
    primary: { icon: KanbanSquare, title: 'Manage pipeline', sub: 'Kanban · activities · AI next-best-action' },
    secondary: [
      { icon: Brain, title: 'Coach a deal (AI)' },
      { icon: PlusCircle, title: 'New opportunity' },
      { icon: CalendarClock, title: 'Schedule activity' },
      { icon: Inbox, title: 'Open the inbox' },
      { icon: Headphones, title: 'Customer support' },
    ],
  },
};

// Default rotation interval; 0 disables cycling (static frame for editor preview).
const DEFAULT_CYCLE_MS = 3600;

export interface TrivenyHeroProps {
  loginUrl: string;
  loginLabel?: string;
  signupUrl?: string;
  /** Override the three pillar statements (defaults = the canonical hero copy). */
  statements?: Partial<Record<Pillar, string>>;
  /** Auto-rotate interval in ms; <=0 holds the first pillar (used in previews). */
  cycleMs?: number;
  /**
   * Contained mode: render inside a sized parent instead of as a full-screen
   * fixed overlay. Used by the Studio editor canvas (so the hero can't escape
   * its container). The public site + full-screen preview leave this false.
   */
  contained?: boolean;
  /** Editable chrome (defaults preserve the canonical hero). */
  getStartedLabel?: string;
  brandName?: string;
  footerNote?: string;
  /** Override the logo image (defaults to the bundled Triveny mark). */
  logoSrc?: string;
  /** Per-pillar launcher overrides (text + the primary icon name). */
  launchers?: Partial<Record<Pillar, { primaryTitle?: string; primarySub?: string; primaryIcon?: string; secondary?: string[] }>>;
}

export default function TrivenyHero({
  loginUrl,
  loginLabel = 'Log in',
  signupUrl,
  statements,
  cycleMs = DEFAULT_CYCLE_MS,
  contained = false,
  getStartedLabel = 'Get started',
  brandName = 'Triveny',
  footerNote = 'All rights reserved',
  logoSrc,
  launchers,
}: TrivenyHeroProps) {
  const S: Record<Pillar, string> = { ...DEFAULT_STATEMENTS, ...(statements || {}) };
  // Merge launcher TEXT overrides over the canonical set (icons stay per slot).
  const effLaunchers = (p: Pillar): LauncherSpec => {
    const base = LAUNCHERS[p];
    const ov = launchers?.[p];
    if (!ov) return base;
    return {
      primary: { icon: resolveIcon(ov.primaryIcon) ?? base.primary.icon, title: ov.primaryTitle ?? base.primary.title, sub: ov.primarySub ?? base.primary.sub },
      secondary: ov.secondary
        ? ov.secondary.map((title, i) => ({ icon: (base.secondary[i] || base.secondary[base.secondary.length - 1]).icon, title }))
        : base.secondary,
    };
  };
  const [active, setActive] = useState<Pillar>('Design');
  // Bumps on every change (auto or manual) so the title re-mounts and replays
  // the fade-in — gives the "tab is changing on top" feel.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (cycleMs <= 0) return;
    const id = window.setTimeout(() => {
      setActive((cur) => PILLAR_ORDER[(PILLAR_ORDER.indexOf(cur) + 1) % PILLAR_ORDER.length]);
      setTick((t) => t + 1);
    }, cycleMs);
    return () => window.clearTimeout(id);
  }, [active, tick, cycleMs]);

  const selectPillar = (p: Pillar) => {
    setActive(p);
    setTick((t) => t + 1);
  };

  return (
    <div className={'triveny-home' + (contained ? ' triveny-home--contained' : '')} data-pillar={active}>
      <div className="stage">
        <nav className="topnav">
          <img
            className="wm"
            src={logoSrc || '/triveny/logo.svg'}
            alt={brandName}
            ref={(el) => {
              if (!el || logoSrc) return; // custom logo: no dark-mode swap
              const isDark = document.documentElement.classList.contains('dark');
              el.src = isDark ? '/triveny/logo-white.svg' : '/triveny/logo.svg';
            }}
            onError={(e) => {
              if (!logoSrc) (e.target as HTMLImageElement).src = '/triveny/wordmark.svg';
            }}
          />
          <div className="nav-center">
            {PILLAR_ORDER.map((pillar) => (
              <button
                key={pillar}
                data-pillar={pillar}
                className={'nav-link' + (active === pillar ? ' active' : '')}
                onClick={() => selectPillar(pillar)}
              >
                {pillar}
              </button>
            ))}
          </div>
          <div className="nav-right" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <a className="signin" href={loginUrl}>{loginLabel}</a>
            {signupUrl && <a className="pub-cta" href={signupUrl}>{getStartedLabel}</a>}
          </div>
        </nav>

        <div className="hero-copy">
          {/* key={active} re-mounts the title so the fade-in replays on each rotation. */}
          <h1 key={active} className="hero-title pub-statement">
            {S[active]}
          </h1>
          {/* Same launcher stack the app home shows post-login (primary action +
              secondary chips). Every launcher routes to login. Re-keyed so it
              fades with the statement on each pillar rotation. */}
          {(() => {
            const L = effLaunchers(active);
            const PrimaryIcon = L.primary.icon;
            return (
              <div key={active + '-cta'} className="launchers pub-statement">
                <a className="launcher primary" href={loginUrl}>
                  <span className="ico">
                    <PrimaryIcon size={16} />
                  </span>
                  <span className="txt">
                    <span className="lt">{L.primary.title}</span>
                    <span className="ls">{L.primary.sub}</span>
                  </span>
                  <span className="arrow">
                    <ArrowRight size={16} />
                  </span>
                </a>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                  {L.secondary.map((s) => {
                    const SIcon = s.icon;
                    return (
                      <a
                        key={s.title}
                        className="launcher"
                        style={{ minWidth: 0, padding: '8px 14px' }}
                        href={loginUrl}
                        title={s.title}
                      >
                        <span
                          className="ico"
                          style={{ width: 24, height: 24, background: 'var(--paper-2)', color: 'var(--ink-700)' }}
                        >
                          <SIcon size={13} />
                        </span>
                        <span className="lt" style={{ fontSize: 13 }}>
                          {s.title}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        <footer className="footer">
          <span>© {new Date().getFullYear()} {brandName}</span>
          <span className="sep">·</span>
          <span>{footerNote}</span>
        </footer>
      </div>
    </div>
  );
}
